import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useRef, useMemo } from 'react';
import { FileIcon } from '@/components/SvgIcon/index';
import { App, Modal, Spin, Button } from 'antd';
import classNames from 'classnames';
import { useVideoPoster } from '../hooks';
import { getMediaLink, getLinkTranscode } from '@/services/chat';
import { fullPath, downloadCrossOriginFile } from '@/utils';
import aegis from '@/utils/aegis';
import { useTranslation } from 'react-i18next';
/** 视频播放失败后，转码轮询的最大重试次数 */ const MAX_TRANSCODE_RETRY = 20;
/** 黑屏检测：轮询间隔(ms) */ const BLACK_FRAME_CHECK_INTERVAL = 1000;
/** 黑屏检测：进度持续推进但画面无新帧，达到该次数后判定为黑屏播放异常 */ const BLACK_FRAME_CHECK_COUNT = 3;
const FileContent = (param)=>{
    let { info } = param;
    const { message, is_dynamic_load, id } = info;
    const app = App.useApp();
    const { t } = useTranslation();
    const videoRef = useRef(null);
    const [open, setOpen] = useState(false);
    /** 文件地址状态 */ const [fileUrl, setFileUrl] = useState(is_dynamic_load ? null : message);
    /** 转码轮询中 */ const [transcoding, setTranscoding] = useState(false);
    /** 转码轮询多次后仍播放失败，视频确认损坏 */ const [videoBroken, setVideoBroken] = useState(false);
    const [posterKey, setPosterKey] = useState(0);
    /** 文件类型/是否视频 */ const isVideo = useMemo(()=>{
        const lower = (fileUrl || message || '').toLowerCase();
        return /\.(mp4|avi|mov|mkv|webm)(\?|$)/i.test(lower) || [
            'mime_type=video',
            'tiktokcdn.com',
            'lazvideo.lazcdn.com'
        ].some((k)=>lower.includes(k));
    }, [
        fileUrl,
        message
    ]);
    /** 视频封面 */ const poster = useVideoPoster(fileUrl || message, posterKey);
    /** 点击播放时，动态请求文件 */ const handlePlay = async ()=>{
        if (is_dynamic_load && id && !fileUrl) {
            try {
                const res = await getMediaLink({
                    jk_chat_log_id: id
                });
                if (res === null || res === void 0 ? void 0 : res.url) setFileUrl(res.url);
            } catch (error) {
                console.error('获取动态文件链接失败:', error);
                aegis.error({
                    msg: `VIDEO_FETCH_ERROR\n\njk_chat_log_id: ${id}`
                });
            }
        }
        /** 如果封面是默认封面，也强制刷新触发封面更新最新的 */ if (isVideo && (!poster || poster.includes('video-cover.png'))) {
            setPosterKey((prev)=>prev + 1);
        }
        setOpen(true);
    };
    /** 弹出层控制视频播放 */ const handleModalClose = ()=>{
        var _videoRef_current;
        setOpen(false);
        (_videoRef_current = videoRef.current) === null || _videoRef_current === void 0 ? void 0 : _videoRef_current.pause();
    };
    /** 视频播放失败：请求转码并轮询状态，
	 * ready成功，transcoding转码中，failed失败
	 * status为ready时更新地址重新播放，
	 * transcoding则继续轮询，failed或轮询超过最大次数仍未ready则提示视频有问题
	 * */ const handleVideoError = async ()=>{
        var /** 转码期间暂停播放，避免黑屏时音频/进度继续 */ _videoRef_current;
        if (!open || !fileUrl || !id || videoBroken || transcoding) return;
        setTranscoding(true);
        (_videoRef_current = videoRef.current) === null || _videoRef_current === void 0 ? void 0 : _videoRef_current.pause();
        try {
            for(let attempt = 1; attempt <= MAX_TRANSCODE_RETRY; attempt++){
                /** 转码有过渡的过程，非首次轮询前先等待再重试 */ if (attempt > 1) {
                    await new Promise((resolve)=>setTimeout(resolve, 3000));
                }
                const res = await getLinkTranscode({
                    jk_chat_log_id: id
                });
                if ((res === null || res === void 0 ? void 0 : res.status) === 'ready' && res.url) {
                    setFileUrl(res.url);
                    return;
                }
                if ((res === null || res === void 0 ? void 0 : res.status) === 'failed') break;
            }
            setVideoBroken(true);
            app.message.error(t('c_s_video_broken'));
        } catch (error) {
            console.error('视频转码失败:', error);
            setVideoBroken(true);
            app.message.error(t('c_s_video_broken'));
        } finally{
            setTranscoding(false);
        }
    };
    /** 部分视频解码失败时浏览器不会抛出error，而是黑屏但继续走播放进度（音轨/时间轴仍在推进），
	 * 因此额外通过“进度在走但已解码视频帧数不再增长”来识别黑屏播放异常，命中后复用转码重试逻辑 */ useEffect(()=>{
        if (!open || !fileUrl || videoBroken) return;
        const video = videoRef.current;
        if (!video) return;
        let noNewFrameCount = 0;
        let lastFrameCount = -1;
        let lastTime = -1;
        const getDecodedFrameCount = ()=>{
            if (typeof video.getVideoPlaybackQuality === 'function') {
                return video.getVideoPlaybackQuality().totalVideoFrames;
            }
            /** 兼容旧版Safari/Firefox私有属性 */ const legacy = video;
            return legacy.webkitDecodedFrameCount ?? legacy.mozDecodedFrames ?? null;
        };
        const timer = setInterval(()=>{
            if (video.paused || video.ended || video.readyState < 2) return;
            const frameCount = getDecodedFrameCount();
            /** 当前浏览器不支持帧数统计，无法检测黑屏 */ if (frameCount === null) return;
            const timeAdvancing = video.currentTime !== lastTime;
            lastTime = video.currentTime;
            if (timeAdvancing && frameCount === lastFrameCount) {
                noNewFrameCount++;
                if (noNewFrameCount >= BLACK_FRAME_CHECK_COUNT) {
                    clearInterval(timer);
                    handleVideoError();
                    return;
                }
            } else {
                noNewFrameCount = 0;
            }
            lastFrameCount = frameCount;
        }, BLACK_FRAME_CHECK_INTERVAL);
        return ()=>clearInterval(timer);
    }, [
        open,
        fileUrl,
        videoBroken
    ]);
    /** 文件名格式化 */ const formatFileName = (fileName)=>{
        var _fileName_split;
        const str = (_fileName_split = fileName.split('/')) === null || _fileName_split === void 0 ? void 0 : _fileName_split.pop();
        if (!str) return '';
        return str.length > 40 ? str.slice(-40) : str;
    };
    useEffect(()=>{
        setFileUrl(is_dynamic_load ? null : message);
        setTranscoding(false);
        setVideoBroken(false);
    }, [
        is_dynamic_load,
        message
    ]);
    if (isVideo) {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "relative w-[180px] h-[100px] rounded-[8px] overflow-hidden bg-black cursor-pointer group",
                    onClick: handlePlay,
                    children: [
                        /*#__PURE__*/ _jsx("img", {
                            src: poster || fullPath('video-cover.png'),
                            alt: "video cover",
                            className: "w-full h-full object-cover transition-transform group-hover:scale-105 duration-300"
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/50",
                            children: /*#__PURE__*/ _jsx("svg", {
                                width: "38",
                                height: "38",
                                viewBox: "0 0 1024 1024",
                                fill: "#fff",
                                children: /*#__PURE__*/ _jsx("path", {
                                    d: "M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zM416 704V320l320 192-320 192z"
                                })
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs(Modal, {
                    open: open,
                    onCancel: handleModalClose,
                    footer: null,
                    width: '90%',
                    centered: true,
                    closeIcon: null,
                    styles: {
                        content: {
                            padding: 0,
                            background: '#000',
                            borderRadius: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            position: 'relative'
                        }
                    },
                    children: [
                        /*#__PURE__*/ _jsx("button", {
                            onClick: handleModalClose,
                            style: {
                                position: 'absolute',
                                top: 16,
                                right: 16,
                                zIndex: 10,
                                background: '#fff',
                                color: '#000',
                                borderRadius: '50%',
                                width: 40,
                                height: 40,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: 'none',
                                cursor: 'pointer'
                            },
                            children: "✕"
                        }),
                        videoBroken ? /*#__PURE__*/ _jsxs("div", {
                            className: "w-full h-[80vh] flex flex-col items-center justify-center gap-[12px] text-white",
                            children: [
                                /*#__PURE__*/ _jsx("img", {
                                    src: fullPath('video-cover.png'),
                                    alt: "",
                                    className: "w-[80px] h-[80px] opacity-60"
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    children: t('c_s_video_broken')
                                }),
                                /*#__PURE__*/ _jsxs(Button, {
                                    onClick: ()=>downloadCrossOriginFile(fileUrl || message, formatFileName(fileUrl || message) || 'video.mp4'),
                                    className: "flex items-center cursor-pointer gap-[6px] px-[16px] py-[8px] mt-[8px] rounded-[6px] bg-black border border-white text-white hover:bg-white/10 transition-colors",
                                    children: [
                                        /*#__PURE__*/ _jsx("svg", {
                                            width: "16",
                                            height: "16",
                                            viewBox: "0 0 20 20",
                                            fill: "none",
                                            children: /*#__PURE__*/ _jsx("path", {
                                                d: "M10 13L6 9L7.0625 7.9375L9.25 10.125V3H10.75V10.125L12.9375 7.9375L14 9L10 13ZM5.4941 16C5.08137 16 4.72917 15.8531 4.4375 15.5594C4.14583 15.2656 4 14.9125 4 14.5V13H5.5V14.5H14.5V13H16V14.5C16 14.9125 15.853 15.2656 15.5591 15.5594C15.2652 15.8531 14.9119 16 14.4992 16H5.4941Z",
                                                fill: "#fff"
                                            })
                                        }),
                                        t('c_s_video_download')
                                    ]
                                })
                            ]
                        }) : /*#__PURE__*/ _jsx("video", {
                            ref: videoRef,
                            src: fileUrl || '',
                            className: "w-full h-[80vh] object-contain",
                            /** 转码期间隐藏原生控件，避免暂停时的播放按钮盖住转码动画 */ controls: !transcoding,
                            autoPlay: true,
                            onError: handleVideoError
                        }, fileUrl),
                        transcoding && /*#__PURE__*/ _jsxs("div", {
                            className: "absolute inset-0 flex flex-col items-center justify-center gap-[12px] z-[5] bg-black/40 pointer-events-none",
                            children: [
                                /*#__PURE__*/ _jsx(Spin, {
                                    size: "large"
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    className: "text-white",
                                    children: t('c_c_video_transcoding')
                                })
                            ]
                        })
                    ]
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("a", {
        href: fileUrl || message,
        target: "_blank",
        rel: "noopener noreferrer",
        className: classNames('flex items-center gap-[8px]'),
        children: [
            /*#__PURE__*/ _jsx("span", {
                className: "flex",
                children: FileIcon
            }),
            /*#__PURE__*/ _jsx("span", {
                className: "text-[#363636] break-all",
                children: formatFileName(fileUrl || message)
            })
        ]
    });
};
export default FileContent;
